import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useDownloadPdf — shared hook for the Download PDF button.
 *
 * Usage:
 *   const { downloading, downloadPdf } = useDownloadPdf();
 *   downloadPdf({ resource: 'lecture-notes', resourceId: unitId, filename: 'my.pdf' });
 */
export function useDownloadPdf() {
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async ({ resource, resourceId, filename, cover }) => {
    if (!resource) return;
    setDownloading(true);
    try {
      const token = sessionStorage.getItem('_t');
      const safeFilename =
        filename ||
        `${resource}-${resourceId || 'document'}.pdf`;
      const res = await axios.post(
        `${API}/print/pdf`,
        { resource, resource_id: resourceId, filename: safeFilename, cover: cover || null },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 180000,
        }
      );
      // res.data is already a Blob when responseType is 'blob' — no need to re-wrap.
      const blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = safeFilename;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      // Defer cleanup so the browser actually starts the download before we
      // revoke the object URL. Some browsers silently drop the download if
      // the URL is revoked too fast after click().
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(blobUrl);
      }, 250);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(`PDF download failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  return { downloading, downloadPdf };
}
